import { useId, useState, type FormEvent } from "react";
import { track } from "@/lib/analytics";
import {
  REPORTED_WAIT_BUCKET_OPTIONS,
  WAIT_REPORT_OUTCOME_OPTIONS,
  submitWaitReport,
  type ReportedWaitBucket,
  type WaitReportOutcome,
} from "@/lib/wait-reports";
import { Button, Notice, Panel, TextField } from "@/components/system";

export function WaitReportForm({
  citySlug,
  facilityId,
}: {
  citySlug: string;
  facilityId: string;
}) {
  const formId = useId();
  const monthBounds = getMonthBounds();
  const [seenBucket, setSeenBucket] = useState<ReportedWaitBucket | null>(null);
  const [outcome, setOutcome] = useState<WaitReportOutcome | null>(null);
  const [referredMonth, setReferredMonth] = useState(monthBounds.max);
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!seenBucket || !outcome) return;
    setState("submitting");

    try {
      await submitWaitReport(citySlug, {
        facilityId,
        seenBucket,
        outcome,
        referredMonth,
        email: email.trim(),
      });
      track("wait_report_submit");
      setState("success");
    } catch {
      setState("error");
    }
  };

  if (state === "success") {
    return (
      <Panel aria-live="polite">
        <p className="ds-eyebrow ds-eyebrow--teal">Anonymous after verification</p>
        <h2 className="ds-title">Check your inbox to verify</h2>
        <p className="ds-small">Your report only counts towards the public figures once you verify it.</p>
      </Panel>
    );
  }

  return (
    <Panel aria-labelledby={`${formId}-heading`}>
      <div className="ds-stack" style={{ gap: 8 }}>
        <p className="ds-eyebrow ds-eyebrow--teal">GP wait-time report</p>
        <h2 id={`${formId}-heading`} className="ds-title">Referred here? Tell other GPs what happened</h2>
        <p className="ds-small">No patient details or free text. Reports appear only in anonymous totals of three or more.</p>
      </div>

      <form onSubmit={handleSubmit} className="ds-stack" style={{ gap: 18 }}>
        <fieldset className="ds-fieldset">
          <legend className="ds-field__label">When was the patient seen?</legend>
          <div className="ds-cluster">
            {REPORTED_WAIT_BUCKET_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="ds-chip"
                aria-pressed={seenBucket === option.value}
                onClick={() => setSeenBucket(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset className="ds-fieldset">
          <legend className="ds-field__label">Referral outcome</legend>
          <div className="ds-cluster">
            {WAIT_REPORT_OUTCOME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="ds-chip"
                aria-pressed={outcome === option.value}
                onClick={() => setOutcome(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </fieldset>

        <div className="ds-form-grid">
          <TextField
            id={`${formId}-month`}
            label="Month referred"
            type="month"
            required
            min={monthBounds.min}
            max={monthBounds.max}
            value={referredMonth}
            onChange={(event) => setReferredMonth(event.target.value)}
          />
          <TextField
            id={`${formId}-email`}
            label="Email, for verification only"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="you@practice.com.au"
          />
        </div>

        {state === "error" ? <Notice tone="error">The report didn't send. Check the fields and try again.</Notice> : null}

        <div className="ds-cluster" style={{ justifyContent: "space-between" }}>
          <p className="ds-fine">Your email is used once to verify this report, then replaced by a one-way hash.</p>
          <Button type="submit" disabled={state === "submitting" || !seenBucket || !outcome || !email.trim()}>
            {state === "submitting" ? "Sending verification…" : "Submit report"}
          </Button>
        </div>
      </form>
    </Panel>
  );
}

function getMonthBounds(): { min: string; max: string } {
  const now = new Date();
  const oldest = new Date(now.getFullYear(), now.getMonth() - 11, 1);
  return {
    min: formatMonth(oldest),
    max: formatMonth(now),
  };
}

function formatMonth(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}
